import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { apiClient, type AuditDbEvent, type AuditDbFilters } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { formatTimestamp } from '@/lib/format';

type Props = {
  /** Locked filters supplied by the parent. */
  filters: AuditDbFilters;
  /** Page size for one fetch. Defaults to the filter's limit or 100. */
  pageSize?: number;
};

/** Filtered audit_db_event table — Postgres trigger sink. No summary; payload is the row snapshot. */
export default function AuditDbEventTable({ filters, pageSize }: Props) {
  const [rows, setRows] = useState<AuditDbEvent[]>([]);
  const [nextBeforeId, setNextBeforeId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [drawerEvent, setDrawerEvent] = useState<AuditDbEvent | null>(null);
  const isMounted = useRef(true);

  const effectiveFilters = useMemo<AuditDbFilters>(
    () => ({ limit: pageSize ?? filters.limit ?? 100, ...filters }),
    [filters, pageSize]
  );

  const loadInitial = useCallback(async () => {
    setIsLoading(true);
    try {
      const page = await apiClient.listAuditDbEvents(effectiveFilters);
      if (!isMounted.current) return;
      setRows(page.events);
      setNextBeforeId(page.next_before_id);
    } catch (err) {
      toast.error('Failed to load DB audit events', { description: String(err) });
    } finally {
      if (isMounted.current) setIsLoading(false);
    }
  }, [effectiveFilters]);

  const loadOlder = useCallback(async () => {
    if (nextBeforeId === null) return;
    setIsLoadingMore(true);
    try {
      const page = await apiClient.listAuditDbEvents({
        ...effectiveFilters,
        before_id: nextBeforeId
      });
      if (!isMounted.current) return;
      setRows((prev) => [...prev, ...page.events]);
      setNextBeforeId(page.next_before_id);
    } catch (err) {
      toast.error('Failed to load more DB events', { description: String(err) });
    } finally {
      if (isMounted.current) setIsLoadingMore(false);
    }
  }, [effectiveFilters, nextBeforeId]);

  useEffect(() => {
    isMounted.current = true;
    loadInitial();
    return () => {
      isMounted.current = false;
    };
  }, [loadInitial]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">No DB audit events match the current filters.</p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">Timestamp</TableHead>
              <TableHead className="w-40">Table</TableHead>
              <TableHead className="w-24">Op</TableHead>
              <TableHead>Row PK</TableHead>
              <TableHead className="w-32">Session user</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => setDrawerEvent(row)}
              >
                <TableCell className="font-mono text-xs">{formatTimestamp(row.ts)}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{row.tableName}</Badge>
                </TableCell>
                <TableCell className="text-xs uppercase">{row.operation}</TableCell>
                <TableCell className="font-mono text-xs">{row.rowPk ?? '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{row.sessionUser}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {nextBeforeId !== null && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={loadOlder} disabled={isLoadingMore}>
            {isLoadingMore ? 'Loading…' : 'Load older'}
          </Button>
        </div>
      )}

      <Sheet open={drawerEvent !== null} onOpenChange={(open) => !open && setDrawerEvent(null)}>
        <SheetContent className="w-[640px] sm:max-w-none overflow-y-auto">
          {drawerEvent && (
            <>
              <SheetHeader>
                <SheetTitle>
                  {drawerEvent.tableName} · {drawerEvent.operation}
                </SheetTitle>
                <SheetDescription>row pk: {drawerEvent.rowPk ?? '—'}</SheetDescription>
              </SheetHeader>
              <dl className="grid grid-cols-3 gap-x-3 gap-y-2 mt-4 text-sm">
                <dt className="font-medium">ID</dt>
                <dd className="col-span-2 font-mono">{drawerEvent.id}</dd>
                <dt className="font-medium">Timestamp</dt>
                <dd className="col-span-2 font-mono">{formatTimestamp(drawerEvent.ts)}</dd>
                <dt className="font-medium">Session user</dt>
                <dd className="col-span-2">{drawerEvent.sessionUser}</dd>
                <dt className="font-medium">Row hash</dt>
                <dd className="col-span-2 font-mono text-xs break-all">{drawerEvent.rowHash}</dd>
              </dl>
              <div className="mt-4">
                <p className="text-sm font-medium mb-2">Row snapshot</p>
                <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(drawerEvent.payload, null, 2)}
                </pre>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
